'use client';

import { FC, useCallback, useEffect } from 'react';
import { useUser } from '@clerk/nextjs';
import { UserResource } from '@clerk/types';
import { analytics } from '@/analytics/segment';
import { datadogRumSetUser } from '@/datadog/rumSetUser';
import { useGhostStore } from '@/legacy/store';
import getUserMetadata from '@/lib/getUserMetadata';

const identifyUser = (
  user: UserResource,
): { email?: string; id: string; firstName?: string; lastName?: string } => {
  return {
    email: user.primaryEmailAddress?.emailAddress,
    id: String((user.publicMetadata?.ghost_id as number | undefined) || user.externalId || user.id),
    firstName: user.firstName || undefined,
    lastName: user.lastName || undefined,
  };
};

const UserInitializer: FC<{ isLocal: boolean }> = ({ isLocal }) => {
  const { setUser } = useGhostStore();
  const { user } = useUser();

  useEffect(() => {
    if (isLocal || !user) return;

    datadogRumSetUser(identifyUser(user));
  }, [user, isLocal]);

  useEffect(() => {
    if (!user) return;

    const { email, id, firstName, lastName } = identifyUser(user);
    analytics.identify(id, { email, firstName, lastName });
  }, [user]);

  const loadUser = useCallback(async () => {
    setUser(await getUserMetadata());
  }, [setUser]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return null;
};

export default UserInitializer;
